import { createRoot } from "react-dom/client";
import { ClerkProvider } from '@clerk/clerk-react';
import { QueryClientProvider } from "@tanstack/react-query";
import App from "./App.tsx";
import { queryClient } from "./lib/queryClient";
import { PerformanceMonitor } from "./lib/performance";

const clerkPubKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

import { initSentry } from "./config/sentry";
import { initAnalytics } from "./services/analytics";
import { restoreQueryCache, persistQueryCache, isIDBAvailable } from "./lib/queryPersistence";
import { prefetchCriticalData } from "./lib/routePrefetch";
import "./i18n/config";
import "./index.css";

// Initialize error tracking
initSentry();

// Initialize analytics
initAnalytics();

// Restore cache from IndexedDB before rendering
const initializeApp = async () => {
  console.log('🚀 Initializing AGXL...');

  // Try to restore cached query state
  if (isIDBAvailable()) {
    try {
      const cachedState = await restoreQueryCache();
      if (cachedState) {
        // Hydrate the query client with cached data
        queryClient.setQueryData(['cache-restored'], true);
        console.log('📦 Cache hydrated from IndexedDB');
      }
    } catch (error) {
      console.warn('⚠️ Cache restoration failed:', error);
    }
  }

  // Render the app
  console.log('📱 Rendering React app...');
  createRoot(document.getElementById("root")!).render(
    <ClerkProvider publishableKey={clerkPubKey}>
      <QueryClientProvider client={queryClient}>
        <App />
        <PerformanceMonitor enabled={false} />
      </QueryClientProvider>
    </ClerkProvider>
  );
  console.log('✅ React app rendered');

  // Set up cache persistence on window unload
  if (isIDBAvailable()) {
    window.addEventListener('beforeunload', () => {
      const state = {
        clientState: {
          queries: queryClient.getQueryCache().getAll().map(query => ({
            queryKey: query.queryKey,
            queryHash: query.queryHash,
            state: query.state,
          })),
          mutations: [],
        },
        timestamp: Date.now(),
        buster: '',
      };
      // Use sync storage for beforeunload (async may not complete)
      try {
        localStorage.setItem('agxl-cache-backup', JSON.stringify(state));
      } catch {
        // Storage full or unavailable
      }
    });

    // Also persist periodically in background
    setInterval(() => {
      const state = {
        clientState: {
          queries: queryClient.getQueryCache().getAll().map(query => ({
            queryKey: query.queryKey,
            queryHash: query.queryHash,
            state: query.state,
          })),
          mutations: [],
        },
        timestamp: Date.now(),
        buster: '',
      };
      persistQueryCache(state as any).catch(() => {});
    }, 60000); // Every minute
  }

  // Prefetch critical data in background
  prefetchCriticalData(queryClient, [
    // Add critical prefetch configs here as the app grows
  ]).catch(() => {});
};

console.log('🚀 Starting app...');
initializeApp();
